import React from 'react';
import { PlusIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui';
import { FOLDER_KIND_LABELS } from '../searchFolderKindUi';
import type { SearchFolderKind } from '../useData';

export type SmartFolderKindCounts = Record<SearchFolderKind, number>;

interface SmartFoldersListHeaderProps {
  totalFolders: number;
  kindCounts: SmartFolderKindCounts;
  onAdd: () => void;
}

const KIND_CHIP_ORDER: SearchFolderKind[] = [
  'search',
  'inbox',
  'combined',
  'sent',
  'combinedSent',
  'mySent',
  'combinedMySent',
];

const SmartFoldersListHeader: React.FC<SmartFoldersListHeaderProps> = ({
  totalFolders,
  kindCounts,
  onAdd,
}) => {
  const { colors } = useTheme();

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
      <div className="flex flex-col gap-4 border-b px-4 py-4 sm:px-5 sm:py-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>Smart folders</h1>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                colors.surfaceHover,
                colors.textSecondary,
              )}
            >
              {totalFolders} total
            </span>
          </div>
          <p className={cn('max-w-2xl text-sm leading-relaxed', colors.textMuted)}>
            Saved mail views with rules, inboxes, and sent folders — shown in the Emails sidebar and shared with your team.
          </p>
        </div>

        <Button type="button" onClick={onAdd} className="shrink-0 self-start">
          <PlusIcon className="h-5 w-5" />
          New smart folder
        </Button>
      </div>

      {totalFolders > 0 && (
        <div className="flex flex-wrap gap-2 px-4 py-3 sm:px-5">
          {KIND_CHIP_ORDER.map((kind) => {
            const count = kindCounts[kind];
            if (count === 0) return null;
            return (
              <span
                key={kind}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium',
                  colors.border,
                  colors.textSecondary,
                  colors.surfaceHover,
                )}
              >
                {FOLDER_KIND_LABELS[kind]}
                <span className="rounded-md bg-black/5 px-1.5 py-0 text-[11px] tabular-nums dark:bg-white/10">
                  {count}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SmartFoldersListHeader;
